import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Box, Container, useMediaQuery } from '@mui/material';
import Navbar from './components/Navbar/Navbar.tsx';
import Sidebar from './components/Sidebar/Sidebar.tsx';
import Home from './components/Home/Home.tsx';
import Header from './components/Header/Header.tsx';
import StickyBox from 'react-sticky-box';
import Footer from './components/Footer/Footer.tsx';
import WelcomePrompt from './components/WelcomePrompt/Welcome';
import UserProfileSettings from './components/Profile/Profile.tsx';
import Login from './components/Login/Login.tsx';
import PrivateRoute from './Auth/PrivateRoute.tsx';
import { AuthProvider, useAuth } from './Auth/AuthProvider.tsx';
import Signup from './components/Login/Signup.tsx'; 
import { UserProfileProvider } from './components/Profile/UserProfileContext';
import TermsOfService from './components/TermsOfService/TermsOfService.tsx'; 
import { CallProvider } from './components/Context/CallContext';
import LoginPage from './components/Login/LoginPage.tsx'; 
import Privacy from './components/TermsOfService/PrivacyPolicy.tsx'; 
import CommunityGuidelines from './components/TermsOfService/CommunityGuidelines';
import AlertBanner from './components/AlertBanner/AlertBanner';

const AppLayout = () => {
  const location = useLocation();
  const isLargeScreen = useMediaQuery('(min-width:1400px)');
  const showHeader = useMediaQuery('(min-width:928px)'); 
  const [showTerms, setShowTerms] = useState(false);
  const { currentUser } = useAuth(); // Get authentication state

  // User profile state
const [userProfile, setUserProfile] = useState({
  ethnicity: "select",
  religion: "select",
  location: "select", 
  politicalView: "select",
  topic: "",
  verified: false,
  veteran: false,
  userName: "Neighbor",
  //userHandle: "@newuser",
  userHandle: "@America"
});

const [alerts, setAlerts] = useState([
  // Example alert - you can add more or fetch from an API
  {
    id: 'welcome-message-launch-2025',  // Unique ID
    message: (
      <span>
        Welcome to UnitedInTalk! We are excited to have you join the conversation. 
        As our community grows, be sure to check out our quick <a href="https://explore.unitedintalk.com/blog/early-user-guide" 
           style={{ color: 'white', textDecoration: 'underline', fontWeight: 'bold' }}
           target="_blank" rel="noopener noreferrer">guide</a> to help you connect with other users.
      </span>
    ),
    severity: 'info',
    active: true
  }
]);

const handleAlertDismiss = (alertId) => {
  setAlerts(alerts.filter(alert => alert.id !== alertId));
};
  
  useEffect(() => {
    // DEVELOPMENT MODE: Always show terms window
    //setShowTerms(true); // Uncomment this for production
    // Check if user has already accepted terms
    const hasAcceptedTerms = localStorage.getItem('termsAccepted') === 'true';
    setShowTerms(!hasAcceptedTerms);
  }, []);
  
  const handleTermsAccept = () => {
    setShowTerms(false);
  };

  return (
    <UserProfileProvider>
      <div>
        {showTerms && location.pathname === '/' && <WelcomePrompt onAccept={handleTermsAccept} />}
        {showHeader && (
          <StickyBox offsetTop={0} offsetBottom={0} style={{ zIndex: 100 }}>
            <Header />
          </StickyBox>
        )}
        
        {/* Alert Banner System */}
        {(location.pathname === '/' || location.pathname === '/profile') && (
          <Container maxWidth={"none"} disableGutters sx={{ pt: 0, margin: '0 auto', }}>
            {alerts.map(alert => (
              <AlertBanner 
                key={alert.id}
                id={alert.id}
                severity={alert.severity}
                message={alert.message}
                onDismiss={() => handleAlertDismiss(alert.id)}
              />
            ))}
          </Container>
        )}
              
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<LoginPage />} />
        </Routes>

        {/* Only render container when NOT on login or signup pages */}
        {location.pathname !== '/login' && location.pathname !== '/signup' && (
          <Container
            maxWidth={"none"}
            disableGutters
            sx={{
              margin: "auto",
              display: 'flex',
              flexDirection: 'row',
              minHeight: 'calc(100vh - 71px)',
              backgroundColor: '#f5f5f5',

            }}
          >
            {/* Navbar rendering unchanged */}
            {!(location.pathname === '/terms' && !currentUser) && !(location.pathname === '/privacy' && !currentUser) && !(location.pathname === '/guidelines' && !currentUser) && <Navbar />}

            <Container maxWidth={"xl"} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1,}}> 
              {/* Rest of your container content */}
              <div style={{display:'flex', flexDirection: 'row'}}>
                <Routes>
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/guidelines" element={<CommunityGuidelines />} />
                  
                  {/* Protected routes */}
                  <Route element={<PrivateRoute />}>
                    <Route path="/" element={<Home userProfile={userProfile} setUserProfile={setUserProfile} />} />
                    <Route path="/profile" element={
                      <Container maxWidth="md" sx={{ py: 4 }}>
                        <UserProfileSettings 
                          userProfile={userProfile} 
                          setUserProfile={setUserProfile} 
                        />
                      </Container> 
                    } />
                  </Route>
                </Routes>
                {isLargeScreen && !(location.pathname === '/terms' && !currentUser) && !(location.pathname === '/privacy' && !currentUser) && !(location.pathname === '/guidelines' && !currentUser) && 
                  <Sidebar userProfile={userProfile}/>}
              </div>
              
              {location.pathname !== '/' && location.pathname !== '/profile' && location.pathname !== '/terms' && location.pathname !== '/privacy' && location.pathname !== '/guidelines' && <Footer/>}
            </Container>
          </Container>
        )}
      </div>
    </UserProfileProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CallProvider>
          <AppLayout />
        </CallProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;